/**
 * RIG mode: rest-skeleton joint placement and the auto-skin (v2) correction parameters.
 * Joint drags are undoable and coalesced; symmetry mirrors the counterpart joint across x
 * (canonical space is x-mirrored: character-left is +X).
 *
 * NONE of the skin actions are undoable: they are solver settings, not document edits, and the
 * rig controller re-solves from the cached geodesic distances on every change (auto-skin v2 A2).
 */

import { CANONICAL_BONE_IDS } from '../../shared';
import type {
  CanonicalBoneId,
  CanonicalRigPlacement,
  SkinJointParams,
  SkinParams,
  SkinResolution,
  Vec3Tuple,
} from '../../shared';
import {
  clampSkinBlend,
  clearJointParams,
  cloneSkinParams,
  defaultSkinParams,
  setJointParams,
} from '../../rig/autoskin/skinParams';
import type { TestPoseId } from '../../rig/autoskin/types';
import { createInitialState } from '../types';
import type { LeakSummary, RigState, SkinTool } from '../types';
import type { ActionContext } from './context';

export interface RigActions {
  setPlacement(placement: CanonicalRigPlacement | null): void;
  moveJoint(bone: CanonicalBoneId, worldPosition: Vec3Tuple, options?: { mirror?: boolean }): void;
  beginJointDrag(bone: CanonicalBoneId): void;
  endJointDrag(): void;
  setSymmetry(symmetry: boolean): void;
  selectJoint(bone: CanonicalBoneId | null): void;
  /** Clearing it also clears the preview pose, the leak summary and the armed tool. */
  setSkinGenerated(generated: boolean): void;
  /** Replace the whole parameter object (project load, `fixLeaks`, revert). */
  setSkinParams(params: SkinParams): void;
  /** Per-joint offset/blend; mirrors the counterpart when `params.symmetry` is on. */
  setSkinJointParams(bone: CanonicalBoneId, patch: Partial<SkinJointParams>): void;
  /** Drop one joint's overrides (mirrored under symmetry) — "Reset joint". */
  resetSkinJoint(bone: CanonicalBoneId): void;
  /** Back to `defaultSkinParams()` — resolution, blend, symmetry, joints and overrides. */
  resetSkinParams(): void;
  setSkinResolution(resolution: SkinResolution): void;
  setSkinSymmetry(symmetry: boolean): void;
  setSkinBlend(blend: number): void;
  /** No-op before the skin exists — there is nothing to preview. */
  setPreviewPose(id: TestPoseId | null): void;
  setLeakSummary(summary: LeakSummary | null): void;
  setSkinTool(tool: SkinTool): void;
  setSkinBusy(busy: boolean): void;
  /**
   * Drop the WHOLE rig slice back to its initial value — every field of it belongs to exactly one
   * project (placement, the generated flag, the solver parameters, the armed tool, the preview
   * pose, the leak report, the selected joint).
   *
   * Deliberately built from `createInitialState()` rather than from a list of `setX(default)`
   * calls: the project-switch reset in `projectController` has to cover the slice by
   * CONSTRUCTION, so a field added to `RigState` later is reset without anyone remembering this
   * call site. Forgetting exactly that is how `placement` survived a project switch and was
   * written onto the next project's record (audit §2.1 #1, P0) — while its neighbours
   * `skinGenerated` and `skinParams`, which were on the hand-written list, were fine.
   *
   * NOT undoable, and it must not be: it runs when the document the undo stack describes is going
   * away, and the caller clears the history in the same breath.
   */
  resetRigState(): void;
}

/** `LeftUpperArm` ⇄ `RightUpperArm`; `null` for centre bones. */
export function mirroredBone(bone: CanonicalBoneId): CanonicalBoneId | null {
  let candidate: string | null = null;
  if (bone.startsWith('Left')) candidate = `Right${bone.slice(4)}`;
  else if (bone.startsWith('Right')) candidate = `Left${bone.slice(5)}`;
  if (!candidate) return null;
  return (CANONICAL_BONE_IDS as readonly string[]).includes(candidate)
    ? (candidate as CanonicalBoneId)
    : null;
}

export function createRigActions(ctx: ActionContext): RigActions {
  const write = (patch: Partial<RigState>): void => {
    ctx.set({ rig: { ...ctx.getState().rig, ...patch } });
  };

  /** Every parameter edit funnels through here, so "params changed" is exactly one subscription. */
  const writeParams = (params: SkinParams): void => {
    write({ skinParams: params });
  };

  return {
    setPlacement(placement): void {
      write({ placement });
    },

    moveJoint(bone, worldPosition, options): void {
      const state = ctx.getState();
      const placement = state.rig.placement;
      if (!placement) return;

      const mirror = options?.mirror ?? state.rig.symmetry;
      const counterpart = mirror ? mirroredBone(bone) : null;

      const positions: Record<CanonicalBoneId, Vec3Tuple> = { ...placement.jointWorldPositions };
      positions[bone] = [worldPosition[0], worldPosition[1], worldPosition[2]];
      if (counterpart && placement.jointWorldPositions[counterpart]) {
        positions[counterpart] = [-worldPosition[0], worldPosition[1], worldPosition[2]];
      }
      const next: CanonicalRigPlacement = { ...placement, jointWorldPositions: positions };

      const apply = (): void => write({ placement: next });
      const revert = (): void => write({ placement });

      apply();
      ctx.history.push({
        label: `Move ${bone}`,
        key: `joint:${bone}`,
        undo: revert,
        redo: apply,
      });
    },

    beginJointDrag(bone): void {
      ctx.history.beginCoalesce(`Move ${bone}`, `joint:${bone}`);
    },

    endJointDrag(): void {
      ctx.history.endCoalesce();
    },

    setSymmetry(symmetry): void {
      if (ctx.getState().rig.symmetry === symmetry) return;
      write({ symmetry });
    },

    selectJoint(bone): void {
      if (ctx.getState().rig.selectedJoint === bone) return;
      write({ selectedJoint: bone });
    },

    setSkinGenerated(generated): void {
      const rig = ctx.getState().rig;
      if (rig.skinGenerated === generated) return;
      // Losing the skin invalidates everything that was measured against it.
      if (!generated) {
        write({ skinGenerated: false, previewPose: null, leakSummary: null, skinTool: 'none' });
        return;
      }
      write({ skinGenerated: true });
    },

    setSkinParams(params): void {
      writeParams(cloneSkinParams(params));
    },

    setSkinJointParams(bone, patch): void {
      writeParams(setJointParams(ctx.getState().rig.skinParams, bone, patch));
    },

    resetSkinJoint(bone): void {
      writeParams(clearJointParams(ctx.getState().rig.skinParams, bone));
    },

    resetSkinParams(): void {
      writeParams(defaultSkinParams());
    },

    setSkinResolution(resolution): void {
      const params = ctx.getState().rig.skinParams;
      if (params.resolution === resolution) return;
      writeParams({ ...cloneSkinParams(params), resolution });
    },

    setSkinSymmetry(symmetry): void {
      const params = ctx.getState().rig.skinParams;
      if (params.symmetry === symmetry) return;
      writeParams({ ...cloneSkinParams(params), symmetry });
    },

    setSkinBlend(blend): void {
      const params = ctx.getState().rig.skinParams;
      // `blend` is a LENGTH in metres (default 0.03, legal 0.002..0.2), not a 0..1 ratio; the
      // bounds live with the solver so every writer applies exactly the same ones.
      const clamped = clampSkinBlend(blend);
      if (params.blend === clamped) return;
      writeParams({ ...cloneSkinParams(params), blend: clamped });
    },

    setPreviewPose(id): void {
      const rig = ctx.getState().rig;
      // Nothing to preview before the weights exist; the gallery is disabled in that state.
      if (!rig.skinGenerated) return;
      if (rig.previewPose === id) return;
      write({ previewPose: id });
    },

    setLeakSummary(summary): void {
      write({ leakSummary: summary });
    },

    setSkinTool(tool): void {
      if (ctx.getState().rig.skinTool === tool) return;
      write({ skinTool: tool });
    },

    setSkinBusy(busy): void {
      if (ctx.getState().rig.skinBusy === busy) return;
      write({ skinBusy: busy });
    },

    resetRigState(): void {
      // Replace, never merge: a merge is a hand-written field list again, and the whole point is
      // that this survives someone adding a field to `RigState`.
      ctx.set({ rig: createInitialState().rig });
    },
  };
}
